import React, { ReactNode, useContext, useEffect, useState } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TrainerProcedureCard from '../TrainerProcedureCard/TrainerProcedureCard';
import { Button, Grid, useTheme } from '@mui/material';
import './TrainerProcedureTab.scss';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useFileUpload } from '../../../../shared/context';
import { CoreProcedureIcon, PreRequisiteIcon } from '../../../Icons/CdsIcons';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import TrainerService from '../../../../services/trainer/TrainerService';
import { useTranslation } from 'react-i18next';
import useCurrentTranslation from '../../../../hooks/useCurrentTranslation';
import moment from 'moment';
import UserContext from '../../../../context/UserContext';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

interface Props {
  procedureDetails: any;
  workInstruction: any;
  status: string;
  isDelete?: boolean;
  setProcedures?: any;
  isRevisitRequest?: boolean;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
      {value === index && (
        <Box component="div">
          <Typography component="span">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const TrainerProcedureTab = (props) => {
  const [value, setValue] = useState(0);
  const [preRequisiteCount, setPreRequisiteCount] = useState<any>();
  const [coreProcedureCount, setCoreProcedureCount] = useState<any>();
  const [buttonText, setButtonText] = useState('Add Prerequisite');
  const [preRequisites, setPreRequisites] = useState(Array<any>);
  const [coreProcedures, setCoreProcedures] = useState(Array<any>);
  const [buttonDisable, setButtonDisable] = useState(false);
  const procedureType = useFileUpload();
  const { t } = useTranslation();

  const theme = useTheme();
  const { companyId } = useParams();

  const reorder = (orderArr) => {
    const reorderBody = {
      id: props.workInstruction.workInstructionId,
      organizationId: companyId,
      order: orderArr,
    };
    TrainerService.reorderProcedure(props.workInstruction.workInstructionId, reorderBody).then((res) => {
      console.log('procedure reordered');
    });
  };

  useEffect(() => {
    if (props.language === 'default') {
      setPreRequisites(props.procedureDetails.filter((data) => data.isPrerequisite === true && !data.language));
      setCoreProcedures(props.procedureDetails.filter((data) => data.isPrerequisite !== true && !data.language));
    } else if (props.language === 'all') {
      setPreRequisites(props.procedureDetails.filter((data) => data.isPrerequisite === true));
      setCoreProcedures(props.procedureDetails.filter((data) => data.isPrerequisite !== true));
    } else {
      setPreRequisites(props.procedureDetails.filter((data) => data.isPrerequisite === true && data.language?.name?.toLowerCase() === props.language?.toLowerCase()));
      setCoreProcedures(props.procedureDetails.filter((data) => data.isPrerequisite !== true && data.language?.name?.toLowerCase() === props.language?.toLowerCase()));
    }
  }, [props.procedureDetails, props.language]);

  useEffect(() => {
    let preRequisiteCount: number = 0,
      coreProcedureCount: number = 0;
    if (props.procedureDetails) {
      for (const procedure of props.procedureDetails) {
        if (procedure.isPrerequisite) {
          preRequisiteCount += 1;
        } else {
          coreProcedureCount += 1;
        }
      }
      setCoreProcedureCount(coreProcedureCount);
      setPreRequisiteCount(preRequisiteCount);
    }
  }, [props.procedureDetails]);

  useEffect(() => {
    if (procedureType.state.isPrerequisite === '' && (preRequisiteCount > 0 || (preRequisiteCount === 0 && coreProcedureCount === 0))) {
      procedureType.dispatch({
        type: 'setProcedureType',
        payload: 'Add Pre Requisite',
      });
    } else if (procedureType.state.isPrerequisite === '' && coreProcedureCount > 0) {
      procedureType.dispatch({
        type: 'setProcedureType',
        payload: 'Add Core Procedure',
      });
    }
  }, [preRequisiteCount, coreProcedureCount, procedureType.state.isPrerequisite]);

  useEffect(() => {
    if (procedureType.state.isPrerequisite.indexOf('Pre Requisite') > -1) {
      setButtonText('Add Prerequisite');
      setValue(0);
    } else if (procedureType.state.isPrerequisite.indexOf('Core Procedure') > -1) {
      setButtonText('Add Core Procedure');
      setValue(1);
    }
  }, [procedureType.state.isPrerequisite]);

  const revisitPath = RegExp('/trainer/RevisitProcedures');

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    if (newValue === 0) {
      setButtonText('Add Prerequisite');
      procedureType.dispatch({
        type: 'setProcedureType',
        payload: 'Add Pre Requisite',
      });
    } else {
      setButtonText('Add Core Procedure');
      procedureType.dispatch({
        type: 'setProcedureType',
        payload: 'Add Core Procedure',
      });
    }
  };
  const populateLabel = (buttonText) => {
    return buttonText === 'Add Prerequisite' ? `${t('BUTTON.ADD')}${' '}${systemPrerequisite}` : `${t('BUTTON.ADD')}${' '}${systemCoreProcedure}`;
  };

  const isExternalPageOpenedHandler = (value) => {
    setButtonDisable(value);
  };

  const reloadPage = () => {
    window.location.reload();
  };

  const location = useLocation();
  const { currentUser } = useContext(UserContext);

  const { currentTranslation } = useCurrentTranslation();
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language.toUpperCase();
  const systemPrerequisite = currentTranslation?.PREREQUISITES ? currentTranslation.PREREQUISITES[currentLanguage] : `${t('PROCEDURE.PRE_REQUISITES')}`;
  const systemCoreProcedure = currentTranslation?.CORE_PROCEDURES ? currentTranslation.CORE_PROCEDURES[currentLanguage] : `${t('PROCEDURE.CORE_PROCEDURES')}`;
  return (
    <Box component="div" sx={{ width: '100%' }}>
      <Box component="div" sx={{ width: '100%' }}>
        <Tabs value={value} onChange={handleChange} aria-label="tabs">
          <Tab
            sx={{
              color: theme.palette.primary.main,
              opacity: '0.5',
              '&.Mui-selected': {
                color: theme.palette.primary.main,
                opacity: '1',
              },
            }}
            className="single-line"
            label={
              <>
                <Box component="span">{systemPrerequisite}</Box>
                {!props.isRevisitRequest && (
                  <Box className="tab-count-border" component="span">
                    {preRequisites.length}{' '}
                  </Box>
                )}
              </>
            }
            {...a11yProps(0)}
          />
          <Tab
            sx={{
              color: theme.palette.primary.main,
              opacity: '0.5',
              '&.Mui-selected': {
                color: theme.palette.primary.main,
                opacity: '1',
              },
            }}
            className="single-line"
            label={
              <>
                <span>{systemCoreProcedure}</span>
                {!props.isRevisitRequest && <span className="tab-count-border">{coreProcedures.length} </span>}
              </>
            }
            {...a11yProps(1)}
          />
          <Box component="div" className="button-align">
            {buttonDisable && (
              <Button variant="contained" onClick={reloadPage}>
                {t('PROCEDURE.REFRESH')}
              </Button>
            )}
            {!props.isRevisitRequest && !props.isDelete && ((buttonText === 'Add Core Procedure' && coreProcedureCount > 0) || (buttonText === 'Add Prerequisite' && !!preRequisiteCount && preRequisiteCount > 0)) && (
              <Link to={`/trainer/addProcedure/${props.workInstruction.workInstructionId}`}>
                <Button
                  disabled={buttonDisable}
                  className="proctab-left-margin"
                  variant="contained"
                  onClick={() => {
                    if (buttonText === 'Add Core Procedure')
                      procedureType.dispatch({
                        type: 'setProcedureType',
                        payload: 'Add Core Procedure',
                      });
                    else
                      procedureType.dispatch({
                        type: 'setProcedureType',
                        payload: 'Add Pre Requisite',
                      });
                  }}
                >
                  {populateLabel(buttonText)}
                </Button>
              </Link>
            )}
          </Box>
        </Tabs>
      </Box>

      <TabPanel value={value} index={0}>
        {!!preRequisiteCount && preRequisiteCount > 0 && (
          <DragDropContext
            onDragEnd={(param) => {
              const srcI = param.source.index;
              const desI = param.destination?.index === undefined ? srcI : param.destination?.index;

              preRequisites.splice(desI, 0, preRequisites.splice(srcI, 1)[0]);

              const pID: Array<string> = [];
              const cID: Array<string> = [];

              preRequisites.map((p, index) => {
                pID.push(p.procedureId);
              });

              coreProcedures.map((p, index) => {
                cID.push(p.procedureId);
              });

              reorder([...pID, ...cID]);
            }}
          >
            <Box component="div" className={'tab-pannel-scroll-no-msg'}>
              <Droppable droppableId="droppable-1">
                {(provided, _) => {
                  return (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {preRequisites && (
                        <div>
                          {preRequisites.map((p, index) => {
                            return (
                              p.isPrerequisite &&
                              ((p?.revisitExpiry !== null && revisitPath.test(location.pathname) && p?.lastRevisitedOn + p?.revisitExpiry * 60 * 60 * 24 < moment().unix() && !p?.revisitComments && p?.revisitSentTo === currentUser.userId) || !revisitPath.test(location.pathname)) && (
                                <Draggable key={p.procedureId} draggableId={p.procedureId.toString()} index={index}>
                                  {(provided, snapshot) => {
                                    return (
                                      <div ref={provided.innerRef} {...provided.draggableProps} className="row">
                                        <div>
                                          <TrainerProcedureCard
                                            isExternalPageOpened={(value) => {
                                              isExternalPageOpenedHandler(value);
                                            }}
                                            provided={provided}
                                            snapshot={snapshot}
                                            status={props.status}
                                            procedureDetails={p}
                                            key={index}
                                            authored={p.isPublished}
                                            title={p.procedureName}
                                            workInstruction={props.workInstruction}
                                            isDelete={props.isDelete}
                                            setProcedures={props.setProcedures}
                                            isRevisitRequest={props.isRevisitRequest}
                                          />
                                        </div>
                                      </div>
                                    );
                                  }}
                                </Draggable>
                              )
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </div>
                  );
                }}
              </Droppable>
            </Box>
          </DragDropContext>
        )}

        {!props.isRevisitRequest && !props.isDelete && !preRequisiteCount && (
          <Grid item xs={12} sm={12} md={12} lg={12} sx={{ marginTop: '3rem' }}>
            <Box component="div">
              <PreRequisiteIcon />
            </Box>
            <Box
              component="div"
              sx={{
                fontSize: '1.5rem',
                lineHeight: '1.5',
                margin: '.8rem 0rem',
              }}
            >
              {t('ADD_WI.PROCEDURE_NO_PRE_REQUISITE', { systemPrerequisite })}
            </Box>
            <Box component="div" sx={{ color: 'rgba(28, 29, 31, 0.6)', marginBottom: '1.3rem' }}>
              {t('PROCEDURE.EMPTY_PRE_REQUISITE_MESSAGE_LINE1', { systemPrerequisite })}
              <br />
              {t('PROCEDURE.EMPTY_PRE_REQUISITE_MESSAGE_LINE2')}
            </Box>
            <Link to={`/trainer/addProcedure/${props.workInstruction.workInstructionId}`}>
              <Button disabled={buttonDisable} className="proctab-left-margin" variant="contained">
                {populateLabel(buttonText)}
              </Button>
            </Link>
          </Grid>
        )}
        {props.isDelete && !preRequisiteCount && (
          <Grid item xs={12} sm={12} md={12} lg={12} sx={{ marginTop: '3rem' }}>
            <Box component="div">
              <PreRequisiteIcon />
            </Box>
            <Box
              component="div"
              sx={{
                fontSize: '1.5rem',
                lineHeight: '1.5',
                margin: '.8rem 0rem',
              }}
            >
              {t('PROCEDURE.NO_PROCEDURE_PRE_REQUISITE_DELETED', { systemPrerequisite })}
            </Box>
            <Box component="div" sx={{ color: 'rgba(28, 29, 31, 0.6)', marginBottom: '1.3rem' }}>
              {t('PROCEDURE.EMPTY_PRE_REQUISITE_MESSAGE_LINE1', { systemPrerequisite })}
              <br />
              {t('PROCEDURE.EMPTY_PRE_REQUISITE_MESSAGE_LINE2')}
            </Box>
          </Grid>
        )}
      </TabPanel>
      <TabPanel value={value} index={1}>
        {coreProcedureCount > 0 && (
          <DragDropContext
            onDragEnd={(param) => {
              const srcI = param.source.index;
              const desI = param.destination?.index === undefined ? srcI : param.destination?.index;
              coreProcedures.splice(desI, 0, coreProcedures.splice(srcI, 1)[0]);

              const pID: Array<string> = [];
              const cID: Array<string> = [];

              coreProcedures.map((p, index) => {
                cID.push(p.procedureId);
              });

              preRequisites.map((p, index) => {
                pID.push(p.procedureId);
              });

              reorder([...pID, ...cID]);
            }}
          >
            <Box component="div" className={'tab-pannel-scroll-no-msg'}>
              <Droppable droppableId="droppable-2">
                {(provided, _) => {
                  return (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {coreProcedures && (
                        <div>
                          {coreProcedures.map((p, index) => {
                            return (
                              !p.isPrerequisite &&
                              ((p?.revisitExpiry !== null && revisitPath.test(location.pathname) && p?.lastRevisitedOn + p?.revisitExpiry * 60 * 60 * 24 < moment().unix() && !p?.revisitComments && p?.revisitSentTo === currentUser.userId) || !revisitPath.test(location.pathname)) && (
                                <Draggable key={p.procedureId} draggableId={p.procedureId.toString()} index={index}>
                                  {(provided, snapshot) => {
                                    return (
                                      <div ref={provided.innerRef} {...provided.draggableProps}>
                                        <TrainerProcedureCard
                                          isExternalPageOpened={(value) => {
                                            isExternalPageOpenedHandler(value);
                                          }}
                                          status={props.status}
                                          procedureDetails={p}
                                          key={index}
                                          provided={provided}
                                          authored={p.isPublished}
                                          title={p.procedureName}
                                          workInstruction={props.workInstruction}
                                          isDelete={props.isDelete}
                                          setProcedures={props.setProcedures}
                                          isRevisitRequest={props.isRevisitRequest}
                                        />
                                      </div>
                                    );
                                  }}
                                </Draggable>
                              )
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </div>
                  );
                }}
              </Droppable>
            </Box>
          </DragDropContext>
        )}
        {!coreProcedureCount && (
          <Grid item xs={12} sm={12} md={12} lg={12} sx={{ marginTop: '3rem' }}>
            <Box component="div">
              <CoreProcedureIcon />
            </Box>
            <Box
              component="div"
              sx={{
                fontSize: '1.5rem',
                lineHeight: '1.5',
                margin: '.8rem 0rem',
              }}
            >
              {!props.isRevisitRequest && !props.isDelete ? `${t('PROCEDURE.NO_CORE', { systemCoreProcedure })}` : `${t('PROCEDURE.NO_CORE_DELETED', { systemCoreProcedure })}`}
            </Box>
            <Box component="div" sx={{ color: 'rgba(28, 29, 31, 0.6)', marginBottom: '1.3rem' }}>
              {!props.isRevisitRequest && !props.isDelete ? `${t('PROCEDURE.EMPTY_CORE_MESSAGE', { systemCoreProcedure })}` : `${t('PROCEDURE.EMPTY_CORE_MESSAGE')}`}
            </Box>
            <Link to={`/trainer/addProcedure/${props.workInstruction.workInstructionId}`}>
              <Button disabled={buttonDisable} className="proctab-left-margin" variant="contained">
                {populateLabel(buttonText)}
              </Button>
            </Link>
          </Grid>
        )}
      </TabPanel>
    </Box>
  );
};

export default TrainerProcedureTab;
